/**
 * Presentation vocabulary for an Agapella recommendation card: the badge that
 * names what the card points at, and the glyph that stands in for missing cover
 * art.
 *
 * Kept in its own module — free of navigation and auth imports — so both the
 * web and the native rendering in `RecommendationCards` read from one table and
 * unit tests can pin it without pulling React Navigation into the test runtime.
 *
 * Contract: awesome-chat-api/docs/rag-implementation.md §4.
 */
import type { RecommendationCard, SourceEntityType } from '../../types/rag';

const ENTITY_LABEL: Partial<Record<SourceEntityType, string>> = {
  master_profile: 'Мастер',
  video_practice: 'Практика',
  live: 'Live',
  master_post: 'Пост',
  master_event: 'Событие',
  platform_event: 'Событие Agape',
  circle_event: 'Событие круга',
  section: 'Раздел',
  platform_material: 'О платформе',
};

const ENTITY_PLACEHOLDER: Partial<Record<SourceEntityType, string>> = {
  master_profile: '☉',
  video_practice: '❖',
  live: '▶',
  master_post: '✎',
  master_event: '✦',
  platform_event: '✦',
  circle_event: '◎',
  section: '❒',
  platform_material: '❔',
};

const KIND_FALLBACK_LABEL: Partial<Record<RecommendationCard['kind'], string>> = {
  master: 'Мастер',
  practice: 'Практика',
  feed: 'Событие',
  section: 'Раздел',
  material: 'О платформе',
};

/**
 * Badge label for a card. The backend can start sending an `entity_type` before
 * the app ships support for it, so an unknown type degrades to the `kind` label
 * and then to a generic word — never to an empty badge.
 */
export function cardBadge(card: RecommendationCard): string {
  return ENTITY_LABEL[card.entity_type] || KIND_FALLBACK_LABEL[card.kind] || 'Контент';
}

/** Glyph shown in place of missing cover art, so a card never collapses to a blank square. */
export function cardPlaceholder(card: RecommendationCard): string {
  return ENTITY_PLACEHOLDER[card.entity_type] ?? '◎';
}
